

































import { mapActions, mapGetters } from 'vuex'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import { SCRIPTS__CREATE_OPTION } from '@/store/actions'
import SchemeCommon from '@/abstract/SchemeCommon'
import CardOptionBadge from '@/components/CardOptionBadge/CardOptionBadge.vue'
import { AppColor } from '@/@types'
import { RouteNames } from '@/router'
import { CardOptionType, CardOptionTypeCreate } from '@/constants/scripts'
import { getRandomElementFromArray } from '@/helpers/random'

import type { AxiosPromise } from 'axios'
import type { RawLocation } from 'vue-router'
import type { Scheme } from '@/models/scheme'
import type { CardOption, CardOptionToCard, CreateOptionPayload, CreateOptionPayloadData, SchemeCardInfo } from '@/models/scripts'
import type { CardVariant } from '@/constants/scripts'

interface Data {
  isOpen: boolean
  isSubmitting: boolean
}

interface Methods {
  [SCRIPTS__CREATE_OPTION]: (payload: CreateOptionPayload) => AxiosPromise<CardOption>
  handleSetOpen: (isOpen: boolean) => void
  handleCreate: (data: CreateOptionPayloadData) => Promise<void>
  handleCreateCard: (card: SchemeCardInfo) => Promise<void>
  handleCreateFinish: () => Promise<void>
}

interface Computed {
  getScriptScheme: Scheme
  listStyle: Partial<CSSStyleDeclaration>
  hasFinish: boolean
  optionsToCards: CardOptionToCard[]
  availableCards: SchemeCardInfo[]
  addOptionRoute: RawLocation
}

interface Props {
  cardId: string
  scriptId: string
  instanceId: string
  variant: CardVariant
  options: CardOption[]
}

export default SchemeCommon.extend<Data, Methods, Computed, Props>({
  components: {
    CardOptionBadge,
  },
  props: {
    cardId: {
      required: true,
    },
    scriptId: {
      required: true,
    },
    instanceId: {
      required: true,
    },
    variant: {
      required: true,
    },
    options: {
      default: () => [],
    },
  },
  data() {
    return {
      isOpen: false,
      isSubmitting: false,
    }
  },
  computed: {
    ...mapGetters(['getScriptScheme']),
    listStyle(): Partial<CSSStyleDeclaration> {
      const { $el } = (this.$refs.popoverElement as Vue) || {}
      return {
        width: !this.isOpen && !$el ? 'auto' : `${$el.clientWidth}px`,
      }
    },
    hasFinish(): boolean {
      return this.options.some(option => option.type === CardOptionType.Final)
    },
    optionsToCards(): CardOptionToCard[] {
      return this.options.filter(option => option.type === CardOptionType.Card) as CardOptionToCard[]
    },
    availableCards(): SchemeCardInfo[] {
      return this.getScriptScheme.cards
        .filter((card: SchemeCardInfo) => {
          return card.id !== this.cardId && this.optionsToCards.every(option => Number(option.link) !== Number(card.num))
        })
    },
    addOptionRoute(): RawLocation {
      return {
        name: RouteNames.ScriptOption,
        params: {
          cardId: this.cardId,
          scriptId: this.scriptId,
          instanceId: this.instanceId,
          variant: this.variant,
        },
      }
    },
  },
  methods: {
    ...mapActions([SCRIPTS__CREATE_OPTION]),
    handleSetOpen(isOpen: boolean): void {
      this.$emit('toggle', isOpen)
      this.isOpen = isOpen
    },
    async handleCreate(data: CreateOptionPayloadData): Promise<void> {
      try {
        if (this.isSubmitting) {
          return
        }

        this.isSubmitting = true

        const payload: CreateOptionPayload = {
          cardId: this.cardId,
          id: this.scriptId,
          instanceId: this.instanceId,
          data,
        }

        await this[SCRIPTS__CREATE_OPTION](payload)

        this.$emit('complete')

        this.isSubmitting = false

        this.$store.dispatch(ADD_TOAST_MESSAGE, {
          text: this.$tc('transition.actions.createSuccess'),
          type: 'success',
        })
      } catch (err) {
        this.isSubmitting = false
      }
    },
    handleCreateCard(card: SchemeCardInfo): Promise<void> {
      const payloadData: CreateOptionPayloadData = {
        variant: this.variant,
        color: getRandomElementFromArray([AppColor.Blue, AppColor.Purple, AppColor.Orange]),
        name: card.name,
        target: false,
        link: card.num,
        type: CardOptionTypeCreate.Card,
      }
      return this.handleCreate(payloadData)
    },
    handleCreateFinish(): Promise<void> {
      const payloadData: CreateOptionPayloadData = {
        variant: this.variant,
        color: AppColor.Red,
        name: this.$tc('common.complete'),
        target: false,
        link: 0,
        type: CardOptionTypeCreate.Final,
      }
      return this.handleCreate(payloadData)
    },
  },
})
